<template>
  <div class="c-input nw-time-picker">
    <input type="hidden" name="working_start_time" />
    <input type="hidden" name="working_end_time" />
    <div class="nw-time-picker__body">
      <div class="nw-time-picker__left">
        <div class="nw-time-picker__steppers">
          <div class="nw-time-picker__stepper-group">
            <div class="nw-time-picker__label">開始</div>
            <button
              type="button"
              class="nw-time-picker__btn"
              @click="incHour('start')"
              @mouseover="$event.currentTarget.style.background = 'rgba(60,182,210,0.1)'"
              @mouseout="$event.currentTarget.style.background = '#fff'"
            >&#9650;</button>
            <div class="nw-time-picker__time-display" :class="{ 'is-placeholder': startHour == null }">
              <input
                class="nw-time-picker__hour-input"
                :class="{ 'is-placeholder': startHour == null }"
                :value="startHour != null ? padMin(startHour) : '07'"
                @focus="onHourFocus('start', $event)"
                @blur="onHourBlur('start', $event)"
                @input="onNumInput($event)"
                @keydown="onFieldKeydown($event)"
              />
              <span class="nw-time-picker__colon">:</span>
              <input
                class="nw-time-picker__min-input"
                :class="{ 'is-placeholder': startHour == null }"
                :value="startHour != null ? padMin(startMinute) : '00'"
                @focus="onMinFocus('start', $event)"
                @blur="onMinBlur('start', $event)"
                @input="onNumInput($event)"
                @keydown="onFieldKeydown($event)"
              />
            </div>
            <button
              type="button"
              class="nw-time-picker__btn"
              @click="decHour('start')"
              @mouseover="$event.currentTarget.style.background = 'rgba(60,182,210,0.1)'"
              @mouseout="$event.currentTarget.style.background = '#fff'"
            >&#9660;</button>
          </div>
          <div class="nw-time-picker__arrow">〜</div>
          <div class="nw-time-picker__stepper-group">
            <div class="nw-time-picker__label">終了</div>
            <button
              type="button"
              class="nw-time-picker__btn"
              @click="incHour('end')"
              @mouseover="$event.currentTarget.style.background = 'rgba(60,182,210,0.1)'"
              @mouseout="$event.currentTarget.style.background = '#fff'"
            >&#9650;</button>
            <div class="nw-time-picker__time-display" :class="{ 'is-placeholder': endHour == null }">
              <input
                class="nw-time-picker__hour-input"
                :class="{ 'is-placeholder': endHour == null }"
                :value="endHour != null ? padMin(endHour) : '23'"
                @focus="onHourFocus('end', $event)"
                @blur="onHourBlur('end', $event)"
                @input="onNumInput($event)"
                @keydown="onFieldKeydown($event)"
              />
              <span class="nw-time-picker__colon">:</span>
              <input
                class="nw-time-picker__min-input"
                :class="{ 'is-placeholder': endHour == null }"
                :value="endHour != null ? padMin(endMinute) : '00'"
                @focus="onMinFocus('end', $event)"
                @blur="onMinBlur('end', $event)"
                @input="onNumInput($event)"
                @keydown="onFieldKeydown($event)"
              />
            </div>
            <button
              type="button"
              class="nw-time-picker__btn"
              @click="decHour('end')"
              @mouseover="$event.currentTarget.style.background = 'rgba(60,182,210,0.1)'"
              @mouseout="$event.currentTarget.style.background = '#fff'"
            >&#9660;</button>
          </div>
        </div>
        <div class="nw-time-picker__hint">数値を直接入力することも可能です。</div>
        <div class="nw-time-picker__btn-group">
          <button type="button" class="nw-time-picker__reset" @click="setDefault">デフォルト</button>
          <button type="button" class="nw-time-picker__reset" @click="set24h">24時間</button>
        </div>
      </div>
      <div class="nw-time-picker__right">
        <svg
          viewBox="0 0 100 100"
          width="180"
          height="180"
          class="nw-time-picker__clock"
        >
          <circle
            cx="50"
            cy="50"
            r="36"
            fill="none"
            stroke="#f1f5f9"
            stroke-width="10"
          />
          <path
            v-if="hasBothValues"
            :d="arcPath"
            fill="none"
            stroke="rgba(60,182,210,0.3)"
            stroke-width="10"
            stroke-linecap="round"
          />
          <template v-for="h in [0, 6, 12, 18]" :key="'h'+h">
            <text
              :x="50 + 47 * Math.cos(toAngle(h, 0))"
              :y="50 + 47 * Math.sin(toAngle(h, 0))"
              text-anchor="middle"
              dominant-baseline="central"
              :font-size="5"
              :fill="hasBothValues && inRange(h) ? '#333' : '#94a3b8'"
              :font-weight="hasBothValues && inRange(h) ? 600 : 400"
            >{{ h }}</text>
          </template>
          <template v-if="startHour != null">
            <circle
              :cx="startMarkerPos.x"
              :cy="startMarkerPos.y"
              r="4.5"
              fill="#3cb6d2"
            />
            <text
              :x="startMarkerPos.x"
              :y="startMarkerPos.y"
              text-anchor="middle"
              dominant-baseline="central"
              font-size="3.5"
              fill="#fff"
              font-weight="700"
            >S</text>
          </template>
          <template v-if="endHour != null">
            <circle
              :cx="endMarkerPos.x"
              :cy="endMarkerPos.y"
              r="4.5"
              fill="#3cb6d2"
            />
            <text
              :x="endMarkerPos.x"
              :y="endMarkerPos.y"
              text-anchor="middle"
              dominant-baseline="central"
              font-size="3.5"
              fill="#fff"
              font-weight="700"
            >E</text>
          </template>
          <template v-if="hasBothValues">
            <text
              x="50"
              y="45"
              text-anchor="middle"
              dominant-baseline="central"
              font-size="6.5"
              fill="#333"
              font-weight="600"
            >{{ fmtTimePad(startHour, startMinute) }}</text>
            <text
              x="50"
              y="52"
              text-anchor="middle"
              dominant-baseline="central"
              font-size="4.5"
              fill="#94a3b8"
            >〜</text>
            <text
              x="50"
              y="59"
              text-anchor="middle"
              dominant-baseline="central"
              font-size="6.5"
              fill="#333"
              font-weight="600"
            >{{ fmtTimePad(endHour, endMinute) }}</text>
          </template>
        </svg>
      </div>
    </div>
    <div class="c-error c-error--balloon"><div class="c-error__text"></div></div>
  </div>
</template>

<script>
export default {
  props: {
    startTime: { type: Number, default: null },
    endTime: { type: Number, default: null },
  },
  emits: ["update:startTime","update:endTime"],
  data: function () {
    return {
      startHour: null,
      startMinute: 0,
      endHour: null,
      endMinute: 0,
    };
  },
  computed: {
    hasBothValues: function () {
      return this.startHour != null && this.endHour != null;
    },
    arcPath: function () {
      if (!this.hasBothValues) return '';
      var a1 = this.toAngle(this.startHour, this.startMinute);
      var a2 = this.toAngle(this.endHour, this.endMinute);
      var R = 36, CX = 50, CY = 50;
      var x1 = CX + R * Math.cos(a1), y1 = CY + R * Math.sin(a1);
      var x2 = CX + R * Math.cos(a2), y2 = CY + R * Math.sin(a2);
      var diff = (this.endHour * 60 + this.endMinute) - (this.startHour * 60 + this.startMinute);
      if (diff <= 0) diff += 1440;
      return 'M ' + x1 + ' ' + y1 + ' A ' + R + ' ' + R + ' 0 ' + (diff > 720 ? 1 : 0) + ' 1 ' + x2 + ' ' + y2;
    },
    startMarkerPos: function () {
      return this.markerPos(this.startHour || 0, this.startMinute || 0);
    },
    endMarkerPos: function () {
      return this.markerPos(this.endHour || 0, this.endMinute || 0);
    },
  },
  watch: {
    startTime: {
      immediate: true,
      handler: function (val) {
        if (val != null) {
          this.startHour = Math.floor(val / 100);
          this.startMinute = val % 100;
        } else {
          this.startHour = null;
          this.startMinute = 0;
        }
      },
    },
    endTime: {
      immediate: true,
      handler: function (val) {
        if (val != null) {
          this.endHour = Math.floor(val / 100);
          this.endMinute = val % 100;
        } else {
          this.endHour = null;
          this.endMinute = 0;
        }
      },
    },
  },
  methods: {
    padMin: function (m) {
      return String(m).padStart(2, '0');
    },
    fmtTimePad: function (h, m) {
      return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
    },
    toHhmm: function (h, m) {
      return h * 100 + m;
    },
    toAngle: function (h, m) {
      return (((h + m / 60) / 24) * 360 - 90) * (Math.PI / 180);
    },
    markerPos: function (h, m) {
      var a = this.toAngle(h, m);
      return { x: 50 + 36 * Math.cos(a), y: 50 + 36 * Math.sin(a) };
    },
    inRange: function (h) {
      if (!this.hasBothValues) return false;
      var sT = this.startHour * 60 + this.startMinute;
      var eT = this.endHour * 60 + this.endMinute;
      var hS = h * 60;
      if (sT <= eT) return hS >= sT && hS < eT;
      return hS >= sT || hS < eT;
    },
    emitStart: function () {
      this.$emit('update:startTime', this.startHour != null ? this.toHhmm(this.startHour, this.startMinute) : null);
    },
    emitEnd: function () {
      this.$emit('update:endTime', this.endHour != null ? this.toHhmm(this.endHour, this.endMinute) : null);
    },
    ensureInitialized: function (side) {
      if (side === 'start' && this.startHour == null) {
        this.startHour = 7;
        this.startMinute = 0;
        return true;
      }
      if (side === 'end' && this.endHour == null) {
        this.endHour = 23;
        this.endMinute = 0;
        return true;
      }
      return false;
    },
    incHour: function (side) {
      if (this.ensureInitialized(side)) {
        if (side === 'start') this.emitStart();
        else this.emitEnd();
        return;
      }
      if (side === 'start') {
        this.startHour = (this.startHour + 1) % 24;
        this.emitStart();
      } else {
        this.endHour = (this.endHour + 1) % 24;
        this.emitEnd();
      }
    },
    decHour: function (side) {
      if (this.ensureInitialized(side)) {
        if (side === 'start') this.emitStart();
        else this.emitEnd();
        return;
      }
      if (side === 'start') {
        this.startHour = (this.startHour - 1 + 24) % 24;
        this.emitStart();
      } else {
        this.endHour = (this.endHour - 1 + 24) % 24;
        this.emitEnd();
      }
    },
    resetAll: function () {
      this.startHour = null;
      this.startMinute = 0;
      this.endHour = null;
      this.endMinute = 0;
      this.emitStart();
      this.emitEnd();
    },
    setDefault: function () {
      this.startHour = 7;
      this.startMinute = 0;
      this.endHour = 23;
      this.endMinute = 0;
      this.emitStart();
      this.emitEnd();
    },
    set24h: function () {
      this.startHour = 0;
      this.startMinute = 0;
      this.endHour = 23;
      this.endMinute = 59;
      this.emitStart();
      this.emitEnd();
    },
    onHourFocus: function (side, e) {
      this.ensureInitialized(side);
      if (side === 'start') this.emitStart();
      else this.emitEnd();
      e.target.select();
    },
    onHourBlur: function (side, e) {
      var n = parseInt(e.target.value, 10);
      if (side === 'start') {
        if (!isNaN(n) && n >= 0 && n <= 23) {
          this.startHour = n;
        }
        e.target.value = this.padMin(this.startHour);
        this.emitStart();
      } else {
        if (!isNaN(n) && n >= 0 && n <= 23) {
          this.endHour = n;
        }
        e.target.value = this.padMin(this.endHour);
        this.emitEnd();
      }
    },
    onMinFocus: function (side, e) {
      this.ensureInitialized(side);
      if (side === 'start') this.emitStart();
      else this.emitEnd();
      e.target.select();
    },
    onMinBlur: function (side, e) {
      var n = parseInt(e.target.value, 10);
      if (side === 'start') {
        if (!isNaN(n) && n >= 0 && n <= 59) {
          this.startMinute = n;
        }
        e.target.value = this.padMin(this.startMinute);
        this.emitStart();
      } else {
        if (!isNaN(n) && n >= 0 && n <= 59) {
          this.endMinute = n;
        }
        e.target.value = this.padMin(this.endMinute);
        this.emitEnd();
      }
    },
    onNumInput: function (e) {
      e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
    },
    onFieldKeydown: function (e) {
      if (e.key === 'Enter') {
        e.target.blur();
      }
    },
  },
};
</script>

<style>
.nw-time-picker {
    border: 1px solid #e5e5e5;
    border-radius: 12px;
    padding: 16px 20px;
    background: #fff;
    max-width: 460px;
    position: relative;
}
.nw-time-picker.is-error {
    border-color: #ea2d2d;
}
.nw-time-picker.is-error .nw-time-picker__time-display {
    border-color: #ea2d2d;
}
.nw-time-picker__body {
    display: flex;
    align-items: flex-start;
    gap: 20px;
}
.nw-time-picker__left {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.nw-time-picker__steppers {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
}
.nw-time-picker__stepper-group {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
}
.nw-time-picker__label {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 500;
    margin-bottom: 2px;
}
.nw-time-picker__btn {
    width: 36px;
    height: 30px;
    border: 1.5px solid rgba(60, 182, 210, 0.3);
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    font-size: 18px;
    color: #3cb6d2;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    font-weight: 500;
    padding: 0;
    line-height: 1;
}
.nw-time-picker__time-display {
    display: flex;
    align-items: baseline;
    padding: 4px 6px;
    border-radius: 6px;
    border: 1px solid #ccc;
    background: #fff;
}
.nw-time-picker__time-display.is-placeholder {
    background: #fff;
}
.nw-time-picker__hour-input {
    width: 32px;
    padding: 2px 0;
    border: none;
    background: transparent;
    font-size: 22px;
    font-weight: 700;
    color: #333;
    text-align: center;
    outline: none;
    font-family: inherit;
}
.nw-time-picker__hour-input.is-placeholder {
    color: #ccc;
}
.nw-time-picker__colon {
    font-size: 22px;
    font-weight: 700;
    color: #333;
    margin: 0 1px;
}
.nw-time-picker__time-display.is-placeholder .nw-time-picker__colon {
    color: #ccc;
}
.nw-time-picker__min-input {
    width: 32px;
    padding: 2px 0;
    border: none;
    background: transparent;
    font-size: 22px;
    font-weight: 700;
    color: #333;
    text-align: center;
    outline: none;
    font-family: inherit;
}
.nw-time-picker__min-input.is-placeholder {
    color: #ccc;
}
.nw-time-picker__arrow {
    padding-top: 18px;
    font-size: 22px;
    color: #cbd5e1;
}
.nw-time-picker__hint {
    margin-top: 8px;
    text-align: center;
    font-size: 10px;
    color: #94a3b8;
}
.nw-time-picker__btn-group {
    display: flex;
    gap: 6px;
    margin-top: 6px;
}
.nw-time-picker__reset {
    font-size: 12px;
    color: #999;
    background: none;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 3px 12px;
    cursor: pointer;
    transition: all 0.15s;
}
.nw-time-picker__reset:hover {
    color: #3cb6d2;
    border-color: rgba(60, 182, 210, 0.3);
    background: rgba(60, 182, 210, 0.1);
}
.nw-time-picker__right {
    flex-shrink: 0;
    display: flex;
    align-items: center;
}
.nw-time-picker__clock {
    display: block;
}
</style>
